import { useState, useEffect, useMemo, useCallback } from 'react';
import { DataGrid, KeenIcon } from '@/components';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { addonValidationSchema } from '@/utils/validations';
import usePagination from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import { ErrorApiResponse } from '@/types/api.types';
import { ConfirmationDialog } from '@/components/dialogBox';
import {
  AddonListQueryParams,
  IAddonById,
  IAddonData,
  PriceUnit,
} from '@/types/addon-management.types';
import {
  useDeleteAddonMutation,
  useGetAddonsQuery,
  useUpdateAddonMutation,
  useGetAddonByIdQuery,
  useCreateAddonMutation,
  useDownloadAddonQrCodeMutation,
} from '@/services/AddonManagementService';
import { AddonModal } from './AddonModal';
import { useGetHarborsQuery } from '@/services/HarborManagementService';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { HarborOption } from '../booking-management/BookingListings';
import Select from 'react-select';
import { useLocation } from 'react-router-dom';
import { formatFinnishCurrency, formatFinnishNumber } from '@/utils';

interface AddonListingsProps {
  harborId?: string;
}

const AddonListings = ({ harborId }: AddonListingsProps) => {
  const { hasPermission } = usePermissions();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    addon: IAddonData | null;
    mode: 'view' | 'edit' | 'create';
  }>({ isOpen: false, addon: null, mode: 'view' });
  const [confirmDeleteState, setConfirmDeleteState] = useState<{
    isOpen: boolean;
    addonId: string | null;
  }>({ isOpen: false, addonId: null });
  const [confirmAvailableState, setConfirmAvailableState] = useState<{
    isOpen: boolean;
    addonId: string | null;
    is_available: boolean;
  }>({ isOpen: false, addonId: null, is_available: false });
  const [availableFilter, setAvailableFilter] = useState<string>('all');
  const [formData, setFormData] = useState<Partial<IAddonData>>({
    harbor_id: harborId || '',
    product_vat_config_id: null,
    name: '',
    description: '',
    price: undefined,
    price_unit: PriceUnit.PER_DAY,
    is_available: true,
    is_standalone: false,
    quantity: undefined,
    access_handover_mode: 'NONE',
    transaction_presentation_mode: null,
    language: 'en',
    translations: {},
    key_box_code: '',
    key_box_pickup_instructions: '',
    key_box_return_instructions: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { enqueueSnackbar } = useSnackbar();
  const [
    updateAddon,
    { isLoading: isUpdating, isSuccess: updateSuccess, isError: updateError },
  ] = useUpdateAddonMutation();
  const [deleteAddon] = useDeleteAddonMutation();
  const [updateAvailableAddon] = useUpdateAddonMutation();
  const [downloadAddonQrCode] = useDownloadAddonQrCodeMutation();
  const [downloadingQrId, setDownloadingQrId] = useState<string | null>(null);

  const handleDownloadQrCode = useCallback(
    async (addon: IAddonData) => {
      try {
        setDownloadingQrId(addon.id as string);
        const blob = await downloadAddonQrCode(addon.id as string).unwrap();
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `addon-${addon.name}-qr.png`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
      } catch {
        enqueueSnackbar('Failed to download QR code', { variant: 'error' });
      } finally {
        setDownloadingQrId(null);
      }
    },
    [downloadAddonQrCode, enqueueSnackbar]
  );
  const [addonIdToFetch, setAddonIdToFetch] = useState<string | null>(null);
  const [harborOptions, setHarborOptions] = useState<HarborOption[]>([]);
  const selectedHarborId = useSelector(
    (state: RootState) => state.auth.selectedHarborId
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const location = useLocation();

  const isHarborDetailsRoute = location.pathname === '/harbors/details';

  // Initialize selectedHarbor with harborId from Redux store
  const [selectedHarbor, setSelectedHarbor] = useState<string>(
    selectedHarborId && isHarborDetailsRoute ? selectedHarborId : ''
  );

  const [harborPagination, setHarborPagination] = useState({
    page: 1,
    limit: 10,
  });

  const { data: harborsData, isLoading: isHarborsLoading } = useGetHarborsQuery(
    {}
  );

  useEffect(() => {
    if (harborsData?.data) {
      const options = harborsData.data.map((harbor) => ({
        value: harbor.id,
        label: harbor.name,
      }));
      setHarborOptions(options);
    }
  }, [harborsData]);

  const handleHarborChange = (selectedOption: HarborOption | null) => {
    const harborId = selectedOption ? selectedOption.value : '';
    setSelectedHarbor(harborId);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const getHarborCountryShortCode = useCallback(
    (currentHarborId?: string) =>
      harborsData?.data?.find((harbor) => harbor.id === currentHarborId)
        ?.country_short_code || '',
    [harborsData]
  );

  const queryParams: AddonListQueryParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue,
    ...(selectedHarbor && { harbor_id: selectedHarbor }),
  };

  if (availableFilter !== 'all') {
    queryParams.is_available = availableFilter === 'available';
  }

  const {
    data: addonsResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetAddonsQuery(queryParams);

  useEffect(() => {
    if (typeof refetch === 'function') {
      refetch();
    }
  }, [debouncedValue, selectedHarbor, refetch]);

  const {
    data: addonResponse,
    isSuccess: addonFetchSuccess,
    error: addonFetchError,
    isFetching: addonFetching,
  } = useGetAddonByIdQuery(addonIdToFetch!, {
    skip: !addonIdToFetch,
  });

  useEffect(() => {
    if (addonsResponse) {
      handleTotalPages(
        addonsResponse.totalPages ?? addonsResponse.total_pages ?? 1
      );
      handleTotalItems(
        addonsResponse.total ??
          addonsResponse.total_items ??
          addonsResponse.totalItems ??
          0
      );
    }
  }, [addonsResponse, handleTotalPages, handleTotalItems]);

  useEffect(() => {
    if (addonFetchSuccess && addonResponse) {
      const addonData: IAddonById = {
        data: {
          id: addonResponse.data.id,
          harbor_id: addonResponse.data.harbor_id,
          name: addonResponse.data.name,
          description: addonResponse.data.description || '',
          product_vat_config_id: addonResponse.data.product_vat_config_id,
          price: addonResponse.data.price || undefined,
          price_unit:
            PriceUnit[
              addonResponse.data.price_unit as keyof typeof PriceUnit
            ] || PriceUnit.PER_DAY,
          is_available: addonResponse.data.is_available || false,
          is_standalone: addonResponse.data.is_standalone || false,
          quantity: addonResponse.data.quantity || undefined,
          transaction_presentation_mode:
            addonResponse.data.transaction_presentation_mode ?? null,
          access_handover_mode:
            addonResponse.data.access_handover_mode ?? 'NONE',
          key_box_code: addonResponse.data.key_box_code ?? '',
          key_box_pickup_instructions:
            addonResponse.data.key_box_pickup_instructions ?? '',
          key_box_return_instructions:
            addonResponse.data.key_box_return_instructions ?? '',
          key_box_instruction_translations:
            addonResponse.data.key_box_instruction_translations ?? undefined,
          language:
            (addonResponse.data as { language?: string }).language ?? 'en',
          translations:
            (
              addonResponse.data as {
                translations?: IAddonData['translations'];
              }
            ).translations ?? {},
          condition: addonResponse.data.condition || undefined,
          conditionUpdatedByUser:
            addonResponse.data.conditionUpdatedByUser || undefined,
          condition_updated_at:
            addonResponse.data.condition_updated_at || undefined,
          harbor: {
            id: addonResponse.data.harbor?.id || '',
            name: addonResponse.data.harbor?.name || '',
          },
          start_time: addonResponse.data.start_time || '',
          end_time: addonResponse.data.end_time || '',
          availability_date_ranges:
            addonResponse.data.availability_date_ranges || [],
        },
      };
      setFormData(addonData.data);
      setErrors({});
      setModalState((prev) => ({
        ...prev,
        isOpen: true,
        addon: addonData.data,
      }));
      setAddonIdToFetch(null);
    }
  }, [addonFetchSuccess, addonResponse]);

  useEffect(() => {
    if (addonFetchError) {
      enqueueSnackbar('Failed to fetch addon details', { variant: 'error' });
    }
  }, [addonFetchError, enqueueSnackbar]);

  useEffect(() => {
    if (updateSuccess) {
      enqueueSnackbar('Addon updated successfully', { variant: 'success' });
      handleCloseModal();
      refetch();
    }
  }, [updateSuccess, enqueueSnackbar, refetch]);

  useEffect(() => {
    if (updateError) {
      enqueueSnackbar('Failed to update addon', { variant: 'error' });
    }
  }, [updateError, enqueueSnackbar]);

  const handleToggleAvailable = async () => {
    if (!confirmAvailableState.addonId) return;
    try {
      await updateAvailableAddon({
        addon_id: confirmAvailableState.addonId,
        data: {
          is_available: confirmAvailableState.is_available,
        },
      }).unwrap();
      enqueueSnackbar({
        message: `Addon ${confirmAvailableState.is_available ? 'made available' : 'made unavailable'}`,
        variant: 'success',
      });
      setConfirmAvailableState({
        isOpen: false,
        addonId: null,
        is_available: false,
      });
      refetch();
    } catch (err) {
      enqueueSnackbar({
        message: (err as ErrorApiResponse).data?.message || 'An error occurred',
        variant: 'error',
      });
      setConfirmAvailableState({
        isOpen: false,
        addonId: null,
        is_available: false,
      });
    }
  };

  const filteredData: IAddonData[] = useMemo(() => {
    if (!addonsResponse?.data) return [];

    return addonsResponse.data.map((item): IAddonData => {
      const addon = item?.data || item;
      return {
        id: addon.id,
        harbor_id: addon.harbor_id,
        name: addon.name || '',
        description: addon.description || '',
        product_vat_config_id: addon.product_vat_config_id,
        price: addon.price,
        price_unit: (addon.price_unit as PriceUnit) || PriceUnit.PER_DAY,
        is_available: addon.is_available || false,
        is_standalone: addon.is_standalone || false,
        quantity: addon.quantity,
        harbor: {
          id: addon.harbor?.id || '',
          name: addon.harbor?.name || '',
        },
        start_time: addon.start_time || '',
        end_time: addon.end_time || '',
        availability_date_ranges: addon.availability_date_ranges || [],
      };
    });
  }, [addonsResponse]);

  const resetFilters = () => {
    if (searchTerm || selectedHarbor) resetSearch();
    setAvailableFilter('all');
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setSelectedHarbor(
      selectedHarborId && isHarborDetailsRoute ? selectedHarborId : ''
    );
  };

  const handleOpenModal = useCallback(
    (addon: IAddonData | null, mode: 'view' | 'edit' | 'create') => {
      if (mode === 'create') {
        setModalState({ isOpen: true, addon: null, mode: 'create' });
        setFormData({
          harbor_id: harborId || '',
          product_vat_config_id: null,
          name: '',
          description: '',
          price: undefined,
          price_unit: PriceUnit.PER_DAY,
          is_available: true,
          is_standalone: false,
          quantity: undefined,
          access_handover_mode: 'NONE',
          transaction_presentation_mode: null,
          key_box_code: '',
          key_box_pickup_instructions: '',
          key_box_return_instructions: '',
          key_box_instruction_translations: {},
          language: 'en',
          translations: {},
          availability_date_ranges: [],
          harbor: { id: '', name: '' },
        });
        setErrors({});
      } else if (addon?.id) {
        setAddonIdToFetch(addon.id);
        setModalState({ isOpen: true, addon, mode });
      }
    },
    [harborId]
  );

  const handleCloseModal = () => {
    setModalState({ isOpen: false, addon: null, mode: 'view' });
    setFormData({});
    setErrors({});
    setAddonIdToFetch(null);
  };
  const [createAddon] = useCreateAddonMutation();

  const handleSubmit = async () => {
    try {
      if (modalState.mode === 'create') {
        await addonValidationSchema.validate(formData, { abortEarly: false });

        const completeRanges = (formData.availability_date_ranges || []).filter(
          (range) => range.start_date && range.end_date
        );
        const payload = {
          ...formData,
          product_vat_config_id:
            getHarborCountryShortCode(formData.harbor_id).toUpperCase() === 'FI'
              ? (formData.product_vat_config_id ?? null)
              : null,
          price: formData.price ? Number(formData.price) : undefined,
          quantity: formData.quantity ? Number(formData.quantity) : undefined,
          transaction_presentation_mode:
            formData.transaction_presentation_mode ?? null,
          availability_date_ranges: completeRanges.length
            ? completeRanges
            : undefined,
        };

        await createAddon(payload).unwrap();
        enqueueSnackbar('Addon created successfully', { variant: 'success' });
        handleCloseModal();
        refetch();
      }
      if (modalState.mode === 'edit' && modalState.addon) {
        const originalData = modalState.addon;
        const payload: Partial<IAddonData> = {};
        const nextProductVatConfigId =
          getHarborCountryShortCode(formData.harbor_id).toUpperCase() === 'FI'
            ? (formData.product_vat_config_id ?? null)
            : null;

        if (formData.harbor_id !== originalData.harbor_id) {
          payload.harbor_id = formData.harbor_id || '';
        }
        if (
          nextProductVatConfigId !==
          (originalData.product_vat_config_id ?? null)
        ) {
          payload.product_vat_config_id = nextProductVatConfigId;
        }
        if (formData.name !== originalData.name) {
          payload.name = formData.name || '';
        }
        if (formData.description !== originalData.description) {
          payload.description = formData.description || '';
        }
        if (formData.price !== originalData.price) {
          payload.price = formData.price ? Number(formData.price) : 0;
        }
        if (formData.price_unit !== originalData.price_unit) {
          payload.price_unit = formData.price_unit || PriceUnit.PER_DAY;
        }
        if (formData.is_available !== originalData.is_available) {
          payload.is_available = formData.is_available || false;
        }
        if (formData.is_standalone !== originalData.is_standalone) {
          payload.is_standalone = formData.is_standalone || false;
        }
        if (formData.quantity !== originalData.quantity) {
          payload.quantity = formData.quantity ? Number(formData.quantity) : 0;
        }
        if (formData.start_time !== originalData.start_time) {
          payload.start_time = formData.start_time || '';
        }
        if (formData.end_time !== originalData.end_time) {
          payload.end_time = formData.end_time || '';
        }
        if (
          JSON.stringify(formData.availability_date_ranges || []) !==
          JSON.stringify(originalData.availability_date_ranges || [])
        ) {
          const completeRanges = (
            formData.availability_date_ranges || []
          ).filter((range) => range.start_date && range.end_date);
          payload.availability_date_ranges = completeRanges.length
            ? completeRanges
            : [];
        }
        if (formData.condition !== originalData.condition) {
          payload.condition = formData.condition || '';
        }
        if (
          formData.transaction_presentation_mode !==
          originalData.transaction_presentation_mode
        ) {
          payload.transaction_presentation_mode =
            formData.transaction_presentation_mode ?? null;
        }
        if (
          formData.access_handover_mode !== originalData.access_handover_mode
        ) {
          payload.access_handover_mode = formData.access_handover_mode;
        }
        if (formData.key_box_code !== originalData.key_box_code) {
          payload.key_box_code = formData.key_box_code;
        }
        if (
          formData.key_box_pickup_instructions !==
          originalData.key_box_pickup_instructions
        ) {
          payload.key_box_pickup_instructions =
            formData.key_box_pickup_instructions;
        }
        if (
          formData.key_box_return_instructions !==
          originalData.key_box_return_instructions
        ) {
          payload.key_box_return_instructions =
            formData.key_box_return_instructions;
        }
        if (
          JSON.stringify(formData.key_box_instruction_translations || {}) !==
          JSON.stringify(originalData.key_box_instruction_translations || {})
        ) {
          (payload as Partial<IAddonData>).key_box_instruction_translations =
            formData.key_box_instruction_translations;
        }
        if (formData.language !== originalData.language) {
          (payload as Partial<IAddonData>).language = formData.language;
        }
        if (
          JSON.stringify(formData.translations || {}) !==
          JSON.stringify(originalData.translations || {})
        ) {
          (payload as Partial<IAddonData>).translations = formData.translations;
        }

        const hasChanges = Object.keys(payload).length > 0;

        if (hasChanges) {
          await updateAddon({
            addon_id: modalState.addon.id as string,
            data: payload,
          }).unwrap();
          handleCloseModal();
        } else {
          enqueueSnackbar({ message: 'No changes to update', variant: 'info' });
          handleCloseModal();
        }
      }
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
        enqueueSnackbar({
          message: `Please fix the form errors (${err.inner.length} errors occurred)`,
          variant: 'error',
        });
      } else {
        const errorMessage =
          (err as ErrorApiResponse).data?.message || 'An error occurred';
        enqueueSnackbar({
          message: errorMessage,
          variant: 'error',
        });
      }
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteState.addonId) return;
    try {
      await deleteAddon(confirmDeleteState.addonId).unwrap();
      enqueueSnackbar({
        message: 'Addon deleted successfully',
        variant: 'success',
      });
      setConfirmDeleteState({ isOpen: false, addonId: null });
      refetch();
    } catch (err) {
      const errorMessage =
        (err as ErrorApiResponse).data?.message || 'An error occurred';
      enqueueSnackbar({
        message: errorMessage,
        variant: 'error',
      });
      setConfirmDeleteState({ isOpen: false, addonId: null });
    }
  };

  const columns = useMemo(
    () => [
      {
        accessor: (row: IAddonData) => row.name,
        id: 'name',
        header: 'Addon Name',
        enableSorting: false,
        cell: ({ row }: { row: { original: IAddonData } }) => (
          <button
            onClick={() => handleOpenModal(row.original, 'view')}
            className="text-sm font-medium text-gray-900 hover:text-primary-active focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label={`View ${row.original.name}`}
          >
            {row.original.name}
          </button>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: IAddonData) => row.harbor?.name,
        id: 'harbor',
        header: 'Harbor',
        enableSorting: false,
        cell: ({ row }: { row: { original: IAddonData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.harbor?.name || 'N/A'}
          </span>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: IAddonData) => row.price,
        id: 'price',
        header: 'Price',
        enableSorting: false,
        cell: ({ row }: { row: { original: IAddonData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.price !== undefined && row.original.price !== null
              ? formatFinnishCurrency(row.original.price)
              : 'N/A'}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: IAddonData) => row.price_unit,
        id: 'price_unit',
        header: 'Unit',
        enableSorting: false,
        cell: ({ row }: { row: { original: IAddonData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.price_unit === PriceUnit.PER_DAY
              ? 'Per Day'
              : row.original.price_unit === PriceUnit.PER_HOUR
                ? 'Per Hour'
                : row.original.price_unit}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: IAddonData) => row.quantity,
        id: 'quantity',
        header: 'Quantity',
        enableSorting: false,
        cell: ({ row }: { row: { original: IAddonData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.quantity !== undefined &&
            row.original.quantity !== null
              ? formatFinnishNumber(row.original.quantity, {
                  maximumFractionDigits: 0,
                })
              : 'N/A'}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: IAddonData) => row.is_available,
        id: 'is_available',
        header: 'Availability',
        enableSorting: false,
        cell: ({ row }: { row: { original: IAddonData } }) => (
          <label
            className="inline-flex items-center cursor-pointer"
            aria-label="Toggle availability"
          >
            <input
              type="checkbox"
              checked={row.original.is_available}
              onChange={() =>
                setConfirmAvailableState({
                  isOpen: true,
                  addonId: row.original.id as string,
                  is_available: !row.original.is_available,
                })
              }
              className="sr-only peer"
              aria-label={`Toggle availability for ${row.original.name}`}
              data-testid="toggle-available"
              disabled={!hasPermission('harbor_addon_management_update')}
            />
            <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            <span className="ml-2 text-sm font-medium text-gray-900">
              {row.original.is_available ? 'ON' : 'OFF'}
            </span>
          </label>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: IAddonData } }) => (
          <div className="flex items-center gap-2">
            {hasPermission('harbor_addon_management_read') && (
              <button
                type="button"
                onClick={() => handleOpenModal(row.original, 'view')}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="View"
                aria-label={`View ${row.original.name}`}
              >
                <KeenIcon type="filled" icon="eye" className="text-gray-500" />
              </button>
            )}
            {hasPermission('harbor_addon_management_update') && (
              <button
                type="button"
                onClick={() => handleOpenModal(row.original, 'edit')}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="Edit"
                aria-label={`Edit ${row.original.name}`}
              >
                <KeenIcon
                  type="filled"
                  icon="pencil"
                  className="text-gray-500"
                />
              </button>
            )}
            {hasPermission('harbor_addon_management_delete') && (
              <button
                type="button"
                onClick={() =>
                  setConfirmDeleteState({
                    isOpen: true,
                    addonId: row.original.id as string,
                  })
                }
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="Delete"
                aria-label={`Delete ${row.original.name}`}
              >
                <KeenIcon type="filled" icon="trash" className="text-red-500" />
              </button>
            )}
            {row.original.is_standalone &&
              hasPermission('harbor_addon_management_read') && (
                <button
                  type="button"
                  onClick={() => handleDownloadQrCode(row.original)}
                  disabled={downloadingQrId === row.original.id}
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  title="Download QR code"
                  aria-label={`Download QR code for ${row.original.name}`}
                >
                  {downloadingQrId === row.original.id ? (
                    <KeenIcon
                      icon="arrows-circle"
                      className="text-gray-500 animate-spin"
                    />
                  ) : (
                    <KeenIcon icon="scan-barcode" className="text-gray-500" />
                  )}
                </button>
              )}
          </div>
        ),
        meta: { className: 'w-[120px]' },
      },
    ],
    [handleOpenModal, hasPermission, handleDownloadQrCode, downloadingQrId]
  );

  const handleOpenCreateModal = () => {
    handleOpenModal(null, 'create');
  };

  return (
    <div className="addon-listings card card-grid w-full">
      <div className="px-6 pt-5">
        <h1 className="text-base font-semibold text-gray-900">
          Addon Management
        </h1>
      </div>
      <div className="card-header addon-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full justify-between">
          <div className="addon-header-left flex items-center gap-2">
            <div className="flex-shrink-0">
              <label
                className="input input-sm h-8 sm:h-10 flex items-center"
                htmlFor="search-addon"
                aria-label="Search addons by name"
              >
                <KeenIcon
                  type="filled"
                  icon="magnifier"
                  className="text-gray-500"
                />
                <input
                  id="search-addon"
                  placeholder="Search addons"
                  type="text"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                  aria-label="Search addons by name"
                />
              </label>
            </div>
            <div className="flex-shrink-0 w-full sm:w-64">
              <Select
                id="harbor-filter"
                options={harborOptions}
                value={
                  harborOptions.find(
                    (option) => option.value === selectedHarbor
                  ) || null
                }
                onChange={handleHarborChange}
                isDisabled={
                  (!!selectedHarborId && isHarborDetailsRoute) || // Disable if harborId is present
                  isLoading ||
                  isFetching ||
                  isUpdating ||
                  isHarborsLoading
                }
                placeholder={
                  isHarborsLoading ? 'Loading harbors...' : 'Select harbor'
                }
                className="react-select-container"
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '0.5rem',
                    borderColor: '#d1d5db',
                    minHeight: '2.5rem',
                    fontSize: '0.875rem',
                    lineHeight: '1.25rem',
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: '#93c5fd',
                    },
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 50,
                    marginTop: '0.5rem',
                    borderRadius: '0.375rem',
                    boxShadow:
                      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  }),
                  option: (base, state) => ({
                    ...base,
                    fontSize: '0.875rem',
                    backgroundColor: state.isSelected
                      ? '#e0f2fe'
                      : state.isFocused
                        ? '#f3f4f6'
                        : 'white',
                    color: '#1f2937',
                    padding: '0.5rem 0.75rem',
                    cursor: 'pointer',
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: '#9ca3af',
                  }),
                }}
                aria-label="Filter bookings by harbor"
              />
              {(harborsData?.total_pages || 0) > 1 && (
                <div className="flex justify-between mt-1">
                  <button
                    disabled={harborPagination.page === 1}
                    onClick={() =>
                      setHarborPagination((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }))
                    }
                    className="btn btn-xs"
                  >
                    Previous
                  </button>
                  <span className="text-xs">
                    Page {harborPagination.page} of{' '}
                    {harborsData?.total_pages || 1}
                  </span>
                  <button
                    disabled={
                      harborPagination.page >= (harborsData?.total_pages || 1)
                    }
                    onClick={() =>
                      setHarborPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                    className="btn btn-xs"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            {isHarborDetailsRoute &&
              hasPermission('harbor_addon_management_create') && (
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={handleOpenCreateModal}
                  aria-label="Add new addon"
                >
                  Add New Addon
                </button>
              )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {searchTerm && searchTerm.trim() !== '' && (
            <button
              data-testid="reset-filters"
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={resetFilters}
              aria-label="Clear all filters"
              title="Clear all filters"
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
              />
              Clear Filters
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        {isLoading || isFetching || addonFetching ? (
          <div className="text-center py-4" role="status" aria-live="polite">
            Loading addons...
          </div>
        ) : isError ? (
          <div className="text-center py-4 text-red-500" role="alert">
            Failed to load addons. Please try again.
          </div>
        ) : (
          <div className="addon-grid-wrapper">
            <DataGrid
              columns={columns}
              data={filteredData}
              rowSelect={false}
              loading={isLoading || isFetching || addonFetching}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                more: true,
                totalItems,
                totalPages,
                setPagination,
                info: `{from} - {to} of {count}`,
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              sorting={[{ id: 'name', desc: false }]}
              serverSide={true}
              onFilterReset={resetFilters}
              hasActiveFilters={
                !!searchTerm || availableFilter !== 'all' || !!selectedHarbor
              }
              aria-label="Addon listings table"
            />
          </div>
        )}
        <AddonModal
          mode={modalState.mode}
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          setErrors={setErrors}
          onSubmit={handleSubmit}
          isSubmitting={isUpdating}
          open={modalState.isOpen}
          onClose={handleCloseModal}
        />
        <ConfirmationDialog
          data-testid="confirmation-dialog"
          open={confirmDeleteState.isOpen}
          onClose={() =>
            setConfirmDeleteState({ isOpen: false, addonId: null })
          }
          onConfirm={handleDelete}
          message="Are you sure you want to delete this addon?"
          cancelButtonText="No"
          confirmButtonText="Yes"
          aria-labelledby="delete-confirmation-title"
          aria-describedby="delete-confirmation-description"
        />
        <ConfirmationDialog
          data-testid="confirmation-dialog"
          open={confirmAvailableState.isOpen}
          onClose={() =>
            setConfirmAvailableState({
              isOpen: false,
              addonId: null,
              is_available: false,
            })
          }
          onConfirm={handleToggleAvailable}
          message={`Are you sure you want to ${confirmAvailableState.is_available ? 'make available' : 'make unavailable'} this addon?`}
          cancelButtonText="No"
          confirmButtonText="Yes"
          aria-labelledby="available-confirmation-title"
          aria-describedby="available-confirmation-description"
        />
      </div>
    </div>
  );
};

export { AddonListings };
